import React, { useMemo, useRef, useEffect, useState } from 'react';
import ForceGraph2D from 'react-force-graph-2d';

const COLOR_LEADER = '#0D1B3D';
const COLOR_LEADER_RING = '#22C55E';
const COLOR_SUPPORTER_STRONG = '#22C55E';
const COLOR_SUPPORTER = '#3B82F6';
const COLOR_SUPPORTER_WEAK = '#CBD5E1';

const supporterColor = (n) => {
  if (n.strength === 'forte') return COLOR_SUPPORTER_STRONG;
  if (n.strength === 'apoiador') return COLOR_SUPPORTER;
  return COLOR_SUPPORTER_WEAK;
};

/**
 * NetworkGraph — interactive force-directed graph (leaders + supporters).
 */
export const NetworkGraph = ({ nodes = [], links = [], filter = {}, onNodeClick, height = 560 }) => {
  const containerRef = useRef(null);
  const fgRef = useRef(null);
  const [size, setSize] = useState({ width: 800, height });

  useEffect(() => {
    if (!containerRef.current) return;
    const obs = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width } = entry.contentRect;
        setSize({ width: Math.max(320, width), height });
      }
    });
    obs.observe(containerRef.current);
    return () => obs.disconnect();
  }, [height]);

  const graphData = useMemo(() => {
    const s = (filter.search || '').toLowerCase().trim();
    const matchesText = (n) => {
      if (!s) return true;
      return `${n.label || ''} ${n.name || ''}`.toLowerCase().includes(s);
    };
    const matchesLoc = (n) => {
      if (filter.estado && n.estado !== filter.estado) return false;
      if (filter.cidade && n.cidade !== filter.cidade) return false;
      return true;
    };
    const matchesTipo = (n) => {
      if (!filter.tipo) return true;
      if (filter.tipo === 'leader') return n.type === 'leader';
      if (filter.tipo === 'supporter') return n.type === 'supporter';
      return true;
    };
    const directMatch = new Set();
    nodes.forEach((n) => {
      if (matchesText(n) && matchesLoc(n) && matchesTipo(n)) directMatch.add(n.id);
    });
    const neighbors = new Set(directMatch);
    links.forEach((l) => {
      const src = typeof l.source === 'object' ? l.source.id : l.source;
      const tgt = typeof l.target === 'object' ? l.target.id : l.target;
      if (directMatch.has(src)) neighbors.add(tgt);
      if (directMatch.has(tgt)) neighbors.add(src);
    });
    const filteredNodes = nodes.filter((n) => neighbors.has(n.id));
    const nodeIds = new Set(filteredNodes.map((n) => n.id));
    const filteredLinks = links.filter((l) => {
      const src = typeof l.source === 'object' ? l.source.id : l.source;
      const tgt = typeof l.target === 'object' ? l.target.id : l.target;
      return nodeIds.has(src) && nodeIds.has(tgt);
    });
    return { nodes: filteredNodes, links: filteredLinks };
  }, [nodes, links, filter.search, filter.estado, filter.cidade, filter.tipo]);

  const renderNode = (node, ctx, globalScale) => {
    if (node.type === 'leader') {
      const r = 9;
      ctx.beginPath();
      ctx.arc(node.x, node.y, r, 0, 2 * Math.PI, false);
      ctx.fillStyle = COLOR_LEADER;
      ctx.fill();
      ctx.lineWidth = 2 / globalScale;
      ctx.strokeStyle = COLOR_LEADER_RING;
      ctx.stroke();
      // Label always visible for leaders
      const fontSize = Math.max(11 / globalScale, 3.5);
      ctx.font = `600 ${fontSize}px Poppins, sans-serif`;
      ctx.fillStyle = '#0F172A';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      ctx.fillText(node.label || '', node.x, node.y + r + 3);
    } else {
      // Supporter: size by centrality (1..n)
      const c = node.centrality || 1;
      const r = Math.min(7, 3 + c * 0.8);
      ctx.beginPath();
      ctx.arc(node.x, node.y, r, 0, 2 * Math.PI, false);
      ctx.fillStyle = supporterColor(node);
      ctx.fill();
      if (globalScale > 1.7) {
        const fontSize = Math.max(9 / globalScale, 3);
        ctx.font = `${fontSize}px Poppins, sans-serif`;
        ctx.fillStyle = '#475569';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'top';
        ctx.fillText(node.label || '', node.x, node.y + r + 2);
      }
    }
  };

  return (
    <div
      ref={containerRef}
      className="w-full rounded-xl overflow-hidden border border-slate-200 bg-white"
      style={{ height }}
      data-testid="network-graph"
    >
      <ForceGraph2D
        ref={fgRef}
        graphData={graphData}
        width={size.width}
        height={size.height}
        backgroundColor="#FAFBFC"
        nodeCanvasObject={renderNode}
        nodeRelSize={4}
        linkColor={(l) => (l.type === 'touch' ? 'rgba(34,197,94,0.35)' : 'rgba(13,27,61,0.18)')}
        linkLineDash={(l) => (l.type === 'touch' ? [4, 3] : null)}
        linkWidth={(l) => (l.type === 'touch' ? 1 : 1.2)}
        cooldownTicks={100}
        d3VelocityDecay={0.3}
        onNodeClick={(node) => onNodeClick && onNodeClick(node)}
        nodeLabel={(n) => `${n.label}${n.cidade ? ' · ' + n.cidade : ''}${n.centrality ? ' · centralidade ' + n.centrality : ''}`}
      />
    </div>
  );
};
